import { useState, useMemo, useEffect, useRef, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "../ui/button";

function convertToSeconds(minutesSeconds) {
  if (minutesSeconds == null) return 0;
  if (typeof minutesSeconds === "number") return minutesSeconds;
  const str = String(minutesSeconds);
  if (!str.includes(":")) {
    const parsed = parseInt(str);
    return isNaN(parsed) ? 0 : parsed;
  }
  let [minutes = "0", seconds = "0"] = str.split(":");
  return parseInt(minutes) * 60 + parseInt(seconds);
}

function formatSeconds(seconds) {
  let minutes = Math.floor(Math.max(0, seconds) / 60);
  let remainder = Math.max(0, seconds) - minutes * 60;
  return (
    String(minutes).padStart(2, "0") +
    ":" +
    String(remainder).padStart(2, "0")
  );
}

function clamp01(n) {
  if (!Number.isFinite(n)) return 0;
  return Math.max(0, Math.min(1, n));
}

const STEP_RING_PALETTE = [
  "#4f46e5",
  "#0ea5e9",
  "#ea580c",
  "#16a34a",
  "#c026d3",
  "#ca8a04",
];

function sanitizeStepColor(raw, index) {
  const fallback = STEP_RING_PALETTE[index % STEP_RING_PALETTE.length];
  if (typeof raw !== "string") return fallback;
  const s = raw.trim();
  if (/^#[0-9A-Fa-f]{3}$/.test(s)) {
    const h = s.slice(1);
    return `#${h[0]}${h[0]}${h[1]}${h[1]}${h[2]}${h[2]}`.toLowerCase();
  }
  if (/^#[0-9A-Fa-f]{6}$/.test(s)) return s.toLowerCase();
  return fallback;
}

const RING_RADIUS = 36;
const RING_STROKE = 11;
const RING_CIRC = 2 * Math.PI * RING_RADIUS;
const VIEW_CX = 50;
const VIEW_CY = 50;

function getSequenceRepeatPolicy(content) {
  const m = content?.sequenceRepeatMode;
  const times = Number(content?.sequenceRepeatTimes);
  const n = Number.isFinite(times) && times >= 1 ? Math.floor(times) : 1;
  if (m === "times") return { mode: "times", times: n };
  if (m === "until_stopped") return { mode: "until_stopped", times: 1 };
  if (m === "once") return { mode: "once", times: 1 };
  if (content?.repeat === true) return { mode: "until_stopped", times: 1 };
  return { mode: "once", times: 1 };
}

function normalizeSteps(rawSteps) {
  if (!Array.isArray(rawSteps)) return [];
  return rawSteps
    .map((s, i) => {
      if (!s || typeof s !== "object") return null;
      const name = typeof s.name === "string" ? s.name.trim() : "";
      const durationSec = Number(s.durationSec);
      if (!name || !Number.isFinite(durationSec) || durationSec <= 0) return null;
      return {
        id: typeof s.id === "string" && s.id ? s.id : `step-${i}`,
        name,
        durationSec: Math.floor(durationSec),
        color: sanitizeStepColor(s.color, i),
      };
    })
    .filter(Boolean);
}

function getTimerModeFromContent(content) {
  const m = content?.timerMode;
  return m === "sequence" ? "sequence" : "single";
}

function totalSequenceSeconds(steps) {
  return steps.reduce((a, s) => a + s.durationSec, 0);
}

/** Polar angle from top (-π/2), clockwise positive in math coords (SVG y-down flips visually). */
function timeToAngle(elapsedSec, totalSec) {
  if (totalSec <= 0) return -Math.PI / 2;
  const p = clamp01(elapsedSec / totalSec);
  return -Math.PI / 2 + p * Math.PI * 2;
}

function polar(cx, cy, r, angle) {
  return {
    x: cx + r * Math.cos(angle),
    y: cy + r * Math.sin(angle),
  };
}

function arcPath(cx, cy, r, a0, a1) {
  const p0 = polar(cx, cy, r, a0);
  const p1 = polar(cx, cy, r, a1);
  const span = a1 - a0;
  const large = Math.abs(span) > Math.PI ? 1 : 0;
  return `M ${p0.x} ${p0.y} A ${r} ${r} 0 ${large} 1 ${p1.x} ${p1.y}`;
}

function normalizeRuntimeState({
  value,
  content,
  mode,
  countdownValue,
  defaultValue,
}) {
  const timerMode = getTimerModeFromContent(content);
  const steps = normalizeSteps(content?.steps);

  if (typeof value === "string") {
    const seconds = convertToSeconds(value);
    return {
      v: 2,
      mode: timerMode,
      running: false,
      paused: false,
      time: formatSeconds(seconds),
      singleInitialSec:
        mode === "down"
          ? Math.max(
              convertToSeconds(countdownValue || defaultValue || "00:00"),
              seconds,
            )
          : null,
      stepIndex: 0,
      stepRemaining: steps[0]?.durationSec ?? 0,
      cycle: 0,
    };
  }

  if (value && typeof value === "object") {
    const desiredMode = timerMode;
    const v2 = {
      v: 2,
      mode: desiredMode,
      running: value.running === true,
      paused: value.paused === true,
      time:
        typeof value.time === "string" && value.time.trim()
          ? value.time.trim()
          : "00:00",
      singleInitialSec:
        typeof value.singleInitialSec === "number" && Number.isFinite(value.singleInitialSec)
          ? value.singleInitialSec
          : mode === "down"
            ? Math.max(
                convertToSeconds(countdownValue || defaultValue || "00:00"),
                convertToSeconds(
                  typeof value.time === "string" ? value.time : "00:00",
                ),
              )
            : null,
      stepIndex: (() => {
        const raw =
          typeof value.stepIndex === "number" && Number.isFinite(value.stepIndex)
            ? Math.max(0, Math.floor(value.stepIndex))
            : 0;
        if (desiredMode !== "sequence") return 0;
        const maxIdx = Math.max(0, steps.length - 1);
        return Math.min(raw, maxIdx);
      })(),
      stepRemaining:
        typeof value.stepRemaining === "number" && Number.isFinite(value.stepRemaining)
          ? Math.max(0, Math.floor(value.stepRemaining))
          : steps[0]?.durationSec ?? 0,
      cycle:
        typeof value.cycle === "number" && Number.isFinite(value.cycle)
          ? Math.max(0, Math.floor(value.cycle))
          : 0,
    };
    if (v2.mode === "sequence" && steps.length > 0) {
      const dur = steps[v2.stepIndex]?.durationSec ?? steps[0].durationSec;
      const lastIdx = steps.length - 1;
      const sequenceComplete =
        v2.stepIndex >= lastIdx && v2.stepRemaining <= 0 && v2.running !== true;
      if (v2.stepRemaining <= 0) {
        if (sequenceComplete) {
          v2.stepRemaining = 0;
          v2.time = "00:00";
          v2.paused = false;
        } else {
          v2.stepRemaining = dur;
          v2.time = formatSeconds(dur);
        }
      }
    }
    return v2;
  }

  if (timerMode === "sequence") {
    return {
      v: 2,
      mode: "sequence",
      running: false,
      paused: false,
      time: formatSeconds(steps[0]?.durationSec ?? 0),
      singleInitialSec: null,
      stepIndex: 0,
      stepRemaining: steps[0]?.durationSec ?? 0,
      cycle: 0,
    };
  }

  const initial =
    mode === "down"
      ? convertToSeconds(countdownValue || defaultValue || "00:00")
      : convertToSeconds(defaultValue || "00:00");
  return {
    v: 2,
    mode: "single",
    running: false,
    paused: false,
    time: formatSeconds(initial),
    singleInitialSec: mode === "down" ? initial : null,
    stepIndex: 0,
    stepRemaining: 0,
    cycle: 0,
  };
}

const Timer = ({
  itemkey,
  content,
  onChange,
  defaultValue,
  value: valueProp,
  mode = "up",
  countdownValue,
  alarmSound = "bell",
  allowDynamicTime = false,
  dynamicTimeLabel = "",
}) => {
  const { t } = useTranslation();
  const rafRef = useRef(null);
  const isControlled = valueProp !== undefined;

  const resolvedAlarmSound =
    alarmSound === true || alarmSound === "bell"
      ? "bell"
      : alarmSound === "beep"
        ? "beep"
        : alarmSound === false || alarmSound == null || alarmSound === ""
          ? null
          : typeof alarmSound === "string"
            ? alarmSound
            : "bell";

  const [dynamicMinutes, setDynamicMinutes] = useState("");
  const [dynamicSeconds, setDynamicSeconds] = useState("");
  const [runtime, setRuntime] = useState(() =>
    normalizeRuntimeState({
      value: valueProp,
      content,
      mode,
      countdownValue,
      defaultValue,
    }),
  );
  const [timerRunning, setTimerRunning] = useState(runtime.running === true);
  const [paused, setPaused] = useState(runtime.paused === true);
  const [timeIsUp, setTimeIsUp] = useState(false);
  const alarmPlayedRef = useRef(false);
  const lastEmittedStateRef = useRef(null);
  const lastEmitSecRef = useRef(null);
  /** Drives smooth display: { kind, anchorMs, singleRem0, seqStepIdx, seqRem0, seqCycle0, singleUpSec0 } */
  const timingRef = useRef(null);
  /** DOM ref for the colored ring circle — written directly in rAF to avoid re-render overhead. */
  const ringCircleRef = useRef(null);
  /** DOM ref for sequence arc paths — keyed by segment index. */
  const seqArcFillRefs = useRef([]);

  const steps = useMemo(() => normalizeSteps(content?.steps), [content]);
  const contentTimerMode = getTimerModeFromContent(content);
  const repeatPolicy = useMemo(() => getSequenceRepeatPolicy(content), [content]);
  const totalCycleSec = useMemo(() => totalSequenceSeconds(steps), [steps]);
  const showTimerDigits = content?.showTimerDigits !== false;

  const dynamicCountdownValue = `${String(dynamicMinutes).padStart(2, "0")}:${String(dynamicSeconds).padStart(2, "0")}`;

  const validDynamicInput =
    dynamicMinutes !== "" &&
    dynamicSeconds !== "" &&
    !isNaN(dynamicMinutes) &&
    !isNaN(dynamicSeconds) &&
    parseInt(dynamicMinutes) >= 0 &&
    parseInt(dynamicSeconds) >= 0 &&
    parseInt(dynamicSeconds) < 60 &&
    (parseInt(dynamicMinutes) > 0 || parseInt(dynamicSeconds) > 0);

  const playAlarm = useCallback(() => {
    if (!resolvedAlarmSound) return;
    let audioSrc;
    if (resolvedAlarmSound === "bell") audioSrc = process.env.PUBLIC_URL + "/assets/sounds/alarm-bell.wav";
    else if (resolvedAlarmSound === "beep") audioSrc = process.env.PUBLIC_URL + "/assets/sounds/beep.wav";
    else audioSrc = resolvedAlarmSound;
    try {
      const audio = new Audio(audioSrc);
      audio.play();
    } catch (e) { }
  }, [resolvedAlarmSound]);

  const emit = useCallback(
    (next) => {
      if (!onChange) return;
      lastEmittedStateRef.current = JSON.stringify(next);
      onChange(content?.key, next);
    },
    [onChange, content?.key],
  );

  const emitIfChanged = useCallback(
    (next) => {
      const key = JSON.stringify(next);
      if (key === lastEmittedStateRef.current) return;
      emit(next);
    },
    [emit],
  );

  useEffect(() => {
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, []);

  useEffect(() => {
    if (!isControlled) return;
    const normalized = normalizeRuntimeState({
      value: valueProp,
      content,
      mode,
      countdownValue,
      defaultValue,
    });
    const stateKey = JSON.stringify(normalized);
    if (stateKey === lastEmittedStateRef.current) return;
    setRuntime(normalized);
    setTimerRunning(normalized.running === true);
    setPaused(normalized.paused === true);
  }, [valueProp, isControlled, content, mode, countdownValue, defaultValue]);

  useEffect(() => {
    if (!onChange) return;
    if (valueProp === undefined || valueProp === null) {
      emit(runtime);
    } else if (typeof valueProp === "string") {
      emit(runtime);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [content?.key]);

  const stepsRef = useRef(steps);
  stepsRef.current = steps;
  const segmentLayoutRef = useRef([]);
  const repeatPolicyRef = useRef(repeatPolicy);
  repeatPolicyRef.current = repeatPolicy;
  const modeRef = useRef(mode);
  modeRef.current = mode;
  const runtimeRef = useRef(runtime);
  runtimeRef.current = runtime;
  const totalCycleSecRef = useRef(totalCycleSec);
  totalCycleSecRef.current = totalCycleSec;

  const currentSecondsInt = useMemo(() => {
    if (runtime.mode === "sequence") return runtime.stepRemaining || 0;
    return convertToSeconds(runtime.time || "00:00");
  }, [runtime]);

  const currentStep = useMemo(() => {
    if (runtime.mode !== "sequence") return null;
    const idx = runtime.stepIndex || 0;
    return steps[idx] || null;
  }, [runtime, steps]);

  /** Static ring metrics for paused / idle states (not called while running). */
  const staticRingMetrics = useMemo(() => {
    const st = steps;
    if (runtime.mode === "sequence" && totalCycleSec > 0) {
      let elapsed = 0;
      for (let i = 0; i < (runtime.stepIndex || 0); i++) {
        elapsed += st[i]?.durationSec ?? 0;
      }
      const dur = currentStep?.durationSec ?? 0;
      const rem = runtime.stepRemaining ?? 0;
      elapsed += dur > 0 ? dur - rem : 0;
      return { prog: clamp01(elapsed / totalCycleSec), seqHeadAngle: timeToAngle(elapsed, totalCycleSec) };
    }
    if (mode === "down") {
      const initial =
        typeof runtime.singleInitialSec === "number" && runtime.singleInitialSec > 0
          ? runtime.singleInitialSec
          : convertToSeconds(countdownValue || defaultValue || "00:00");
      const rem = currentSecondsInt;
      return { prog: initial > 0 ? clamp01(1 - rem / initial) : 0, seqHeadAngle: -Math.PI / 2 };
    }
    const sec = currentSecondsInt;
    return { prog: clamp01((sec % 60) / 60), seqHeadAngle: -Math.PI / 2 };
  }, [runtime, mode, currentSecondsInt, currentStep, countdownValue, defaultValue, totalCycleSec, steps]);

  // Always use staticRingMetrics for React render — live animation is driven via DOM refs.
  const effectiveProg = staticRingMetrics.prog;
  const effectiveHeadAngle = staticRingMetrics.seqHeadAngle;

  const ringColor = useMemo(() => {
    if (timeIsUp && runtime.mode === "single" && mode === "down") return "#dc2626";
    if (contentTimerMode === "sequence" && currentStep?.color) return currentStep.color;
    return "#4f46e5";
  }, [contentTimerMode, runtime.mode, currentStep, mode, timeIsUp]);

  const singleRingDashOffset = RING_CIRC * (1 - effectiveProg);

  const segmentLayout = useMemo(() => {
    if (steps.length === 0 || totalCycleSec <= 0) { segmentLayoutRef.current = []; return []; }
    let cum = 0;
    const layout = steps.map((s) => {
      const t0 = cum;
      cum += s.durationSec;
      const t1 = cum;
      const a0 = timeToAngle(t0, totalCycleSec);
      const a1 = timeToAngle(t1, totalCycleSec);
      return { ...s, t0, t1, a0, a1 };
    });
    segmentLayoutRef.current = layout;
    return layout;
  }, [steps, totalCycleSec]);

  const cancelRaf = useCallback(() => {
    if (rafRef.current) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
  }, []);

  /** Stable ref so the rAF loop always calls the latest runFrame without restarting the loop. */
  const runFrameRef = useRef(null);

  const runFrame = useCallback(() => {
    const T = timingRef.current;
    if (!T || !runtimeRef.current.running) {
      return;
    }

    const now = Date.now();

    if (T.kind === "single-down") {
      const elapsed = (now - T.anchorMs) / 1000;
      const rem = Math.max(0, T.rem0 - elapsed);
      const sec = Math.floor(rem + 1e-6);
      const prev = runtimeRef.current;
      if (sec !== convertToSeconds(prev.time || "00:00")) {
        const next = { ...prev, time: formatSeconds(sec) };
        runtimeRef.current = next;
        setRuntime(next);
        if (sec !== lastEmitSecRef.current) {
          lastEmitSecRef.current = sec;
          emitIfChanged(next);
        }
      }
      // Write ring progress directly to DOM for smooth animation without re-renders.
      const prog = T.initial > 0 ? clamp01(1 - rem / T.initial) : 0;
      if (ringCircleRef.current) {
        ringCircleRef.current.style.strokeDashoffset = String(RING_CIRC * (1 - prog));
      }
      if (rem <= 0.001) {
        timingRef.current = null;
        cancelRaf();
        const next = { ...runtimeRef.current, running: false, paused: false, time: "00:00" };
        runtimeRef.current = next;
        setRuntime(next);
        setTimerRunning(false);
        if (ringCircleRef.current) ringCircleRef.current.style.strokeDashoffset = String(0);
        emitIfChanged(next);
        if (!alarmPlayedRef.current) {
          alarmPlayedRef.current = true;
          playAlarm();
          setTimeIsUp(true);
        }
        return;
      }
    } else if (T.kind === "single-up") {
      const elapsed = (now - T.anchorMs) / 1000;
      const sec = Math.floor(T.sec0 + elapsed);
      const prev = runtimeRef.current;
      if (sec !== convertToSeconds(prev.time || "00:00")) {
        const next = { ...prev, time: formatSeconds(sec) };
        runtimeRef.current = next;
        setRuntime(next);
        if (sec !== lastEmitSecRef.current) {
          lastEmitSecRef.current = sec;
          emitIfChanged(next);
        }
      }
      const floatSec = T.sec0 + elapsed;
      if (ringCircleRef.current) {
        const prog = clamp01((floatSec % 60) / 60);
        ringCircleRef.current.style.strokeDashoffset = String(RING_CIRC * (1 - prog));
      }
    } else if (T.kind === "sequence") {
      const st = stepsRef.current;
      if (st.length === 0) {
        rafRef.current = requestAnimationFrame(() => runFrameRef.current());
        return;
      }
      let rem = T.rem0 - (Date.now() - T.stepAnchorMs) / 1000;
      let idx = T.stepIdx;

      while (rem <= 0) {
        const overflow = -rem;
        if (idx < st.length - 1) {
          idx += 1;
          T.stepIdx = idx;
          T.rem0 = st[idx].durationSec;
          T.stepAnchorMs = Date.now();
          rem = T.rem0 - overflow;
          const sec = Math.max(0, Math.ceil(rem - 1e-9));
          const next = {
            ...runtimeRef.current,
            stepIndex: idx,
            stepRemaining: sec,
            time: formatSeconds(sec),
          };
          runtimeRef.current = next;
          setRuntime(next);
          emitIfChanged(next);
          lastEmitSecRef.current = sec;
          continue;
        }
        const newCycle = (runtimeRef.current.cycle || 0) + 1;
        const pol = repeatPolicyRef.current;
        if (pol.mode === "until_stopped") {
          idx = 0;
          T.stepIdx = 0;
          T.rem0 = st[0].durationSec;
          T.stepAnchorMs = Date.now();
          rem = T.rem0 - overflow;
          const sec = Math.max(0, Math.ceil(rem - 1e-9));
          const next = {
            ...runtimeRef.current,
            stepIndex: 0,
            stepRemaining: sec,
            time: formatSeconds(sec),
            cycle: newCycle,
          };
          runtimeRef.current = next;
          setRuntime(next);
          emitIfChanged(next);
          lastEmitSecRef.current = sec;
          continue;
        }
        if (pol.mode === "once" || (pol.mode === "times" && newCycle >= pol.times)) {
          timingRef.current = null;
          cancelRaf();
          const next = {
            ...runtimeRef.current,
            running: false,
            paused: false,
            stepIndex: st.length - 1,
            stepRemaining: 0,
            time: "00:00",
            cycle: newCycle,
          };
          runtimeRef.current = next;
          setRuntime(next);
          setTimerRunning(false);
          emitIfChanged(next);
          if (resolvedAlarmSound) queueMicrotask(() => playAlarm());
          return;
        }
        idx = 0;
        T.stepIdx = 0;
        T.rem0 = st[0].durationSec;
        T.stepAnchorMs = Date.now();
        rem = T.rem0 - overflow;
        const sec = Math.max(0, Math.ceil(rem - 1e-9));
        const next = {
          ...runtimeRef.current,
          stepIndex: 0,
          stepRemaining: sec,
          time: formatSeconds(sec),
          cycle: newCycle,
        };
        runtimeRef.current = next;
        setRuntime(next);
        emitIfChanged(next);
        lastEmitSecRef.current = sec;
      }

      const sec = Math.max(0, Math.ceil(rem - 1e-9));
      const prev = runtimeRef.current;
      if (sec !== (prev.stepRemaining ?? 0)) {
        const next = { ...prev, stepRemaining: sec, time: formatSeconds(sec) };
        runtimeRef.current = next;
        setRuntime(next);
        if (sec !== lastEmitSecRef.current) {
          lastEmitSecRef.current = sec;
          emitIfChanged(next);
        }
      }
      // Write sequence arc fills directly to DOM for smooth animation without re-renders.
      const tot = totalCycleSecRef.current;
      if (tot > 0) {
        let elapsedBefore = 0;
        for (let i = 0; i < T.stepIdx; i++) elapsedBefore += st[i]?.durationSec ?? 0;
        const d = st[T.stepIdx]?.durationSec ?? 0;
        const elapsedFloat = Math.min(tot, elapsedBefore + (d - Math.max(0, rem)));
        const headAngleLive = timeToAngle(elapsedFloat, tot);
        // Update each fill arc path directly
        seqArcFillRefs.current.forEach((el, i) => {
          if (!el) return;
          const seg = segmentLayoutRef.current[i];
          if (!seg) return;
          if (headAngleLive <= seg.a0) { el.setAttribute("d", ""); return; }
          const a1 = headAngleLive >= seg.a1 ? seg.a1 : headAngleLive;
          if (a1 <= seg.a0) { el.setAttribute("d", ""); return; }
          el.setAttribute("d", arcPath(VIEW_CX, VIEW_CY, RING_RADIUS, seg.a0, a1));
        });
      }
    }

    rafRef.current = requestAnimationFrame(() => runFrameRef.current());
  }, [cancelRaf, emitIfChanged, playAlarm, resolvedAlarmSound]);

  runFrameRef.current = runFrame;

  useEffect(() => {
    if (!timerRunning) {
      cancelRaf();
      return;
    }
    rafRef.current = requestAnimationFrame(() => runFrameRef.current());
    return () => cancelRaf();
  }, [timerRunning, cancelRaf]);

  const handleStart = useCallback(() => {
    if (contentTimerMode === "sequence" && steps.length === 0) return;

    const now = Date.now();
    lastEmitSecRef.current = null;
    alarmPlayedRef.current = false;

    setRuntime((prev) => {
      let next = { ...prev, running: true, paused: false };
      if (prev.mode === "sequence" && steps.length > 0) {
        const rem = prev.stepRemaining ?? 0;
        const atEnd =
          !prev.running && rem <= 0 && prev.stepIndex >= Math.max(0, steps.length - 1);
        if (rem <= 0 || atEnd) {
          const firstDur = steps[0].durationSec;
          next = {
            ...next,
            stepIndex: 0,
            stepRemaining: firstDur,
            time: formatSeconds(firstDur),
            cycle: 0,
          };
        }
        const idx = next.stepIndex || 0;
        // Use the exact float remainder saved at pause time to avoid ring jump-back.
        const sr =
          typeof next.stepRemainingFloat === "number" && next.stepRemainingFloat > 0
            ? next.stepRemainingFloat
            : (next.stepRemaining ?? steps[idx].durationSec);
        next = { ...next, stepRemainingFloat: undefined };
        timingRef.current = {
          kind: "sequence",
          stepIdx: idx,
          rem0: sr,
          stepAnchorMs: now,
        };
      } else if (mode === "down") {
        const initial =
          typeof next.singleInitialSec === "number" && next.singleInitialSec > 0
            ? next.singleInitialSec
            : convertToSeconds(countdownValue || defaultValue || "00:00");
        // If time is at zero (completed), restart from the full initial duration.
        const sec = convertToSeconds(next.time || "00:00");
        const rem = sec > 0 ? sec : initial;
        next = { ...next, time: formatSeconds(rem), singleInitialSec: initial };
        timingRef.current = { kind: "single-down", anchorMs: now, rem0: rem, initial };
      } else {
        // Count-up: continue from the current saved value.
        const sec = convertToSeconds(next.time || "00:00");
        timingRef.current = { kind: "single-up", anchorMs: now, sec0: sec };
      }
      // Keep runtimeRef in sync immediately so runFrame sees running:true on the first tick.
      runtimeRef.current = next;
      emit(next);
      return next;
    });
    setTimerRunning(true);
    setPaused(false);
    setTimeIsUp(false);
    // Reset ring to empty so it fills from scratch on restart.
    if (ringCircleRef.current) {
      ringCircleRef.current.style.strokeDashoffset = String(RING_CIRC);
    }
  }, [emit, mode, contentTimerMode, steps, countdownValue, defaultValue]);

  const handlePause = useCallback(() => {
    cancelRaf();
    const T = timingRef.current;
    const now = Date.now();

    setRuntime((prev) => {
      let next = { ...prev, running: false, paused: true };
      if (T?.kind === "single-down") {
        const elapsed = (now - T.anchorMs) / 1000;
        const rem = Math.max(0, T.rem0 - elapsed);
        next = { ...next, time: formatSeconds(Math.max(0, Math.ceil(rem - 1e-6))) };
      } else if (T?.kind === "single-up") {
        const elapsed = (now - T.anchorMs) / 1000;
        const sec = Math.floor(T.sec0 + elapsed);
        next = { ...next, time: formatSeconds(sec) };
      } else if (T?.kind === "sequence") {
        const idx = T.stepIdx;
        const remFloat = Math.max(0, T.rem0 - (now - T.stepAnchorMs) / 1000);
        const remInt = Math.max(0, Math.ceil(remFloat - 1e-6));
        next = {
          ...next,
          stepIndex: idx,
          stepRemaining: remInt,
          stepRemainingFloat: remFloat,
          time: formatSeconds(remInt),
          cycle: prev.cycle || 0,
        };
      }
      timingRef.current = null;
      emit(next);
      return next;
    });
    setTimerRunning(false);
    setPaused(true);
  }, [emit, cancelRaf]);

  const handleTimerStopReset = () => {
    cancelRaf();
    timingRef.current = null;
    setTimerRunning(false);
    setPaused(false);
    setTimeIsUp(false);
    // For count-up timers, Stop should NOT reset the elapsed value.
    if (runtimeRef.current.mode === "single" && modeRef.current === "up") {
      setRuntime((prev) => {
        const next = { ...prev, running: false, paused: false };
        runtimeRef.current = next;
        emit(next);
        return next;
      });
      return;
    }
    // Clear the ring fill back to empty.
    if (ringCircleRef.current) {
      ringCircleRef.current.style.strokeDashoffset = String(RING_CIRC);
    }
    seqArcFillRefs.current.forEach((el) => { if (el) el.setAttribute("d", ""); });
    const next = normalizeRuntimeState({
      value: null,
      content,
      mode,
      countdownValue,
      defaultValue,
    });
    setRuntime(next);
    emit(next);
  };

  const handleResetCountUp = useCallback(() => {
    cancelRaf();
    timingRef.current = null;
    setTimerRunning(false);
    setPaused(false);
    setTimeIsUp(false);
    if (ringCircleRef.current) {
      ringCircleRef.current.style.strokeDashoffset = String(RING_CIRC);
    }
    setRuntime((prev) => {
      const next = { ...prev, running: false, paused: false, time: "00:00" };
      runtimeRef.current = next;
      emit(next);
      return next;
    });
  }, [cancelRaf, emit]);

  const handleStartPauseButton = () => {
    if (timerRunning) handlePause();
    else handleStart();
  };

  const handleSetButton = () => {
    if (!allowDynamicTime || !validDynamicInput) return;
    const nextSec = convertToSeconds(dynamicCountdownValue);
    setRuntime((prev) => {
      const next = {
        ...prev,
        mode: prev.mode === "sequence" ? "sequence" : "single",
        running: false,
        paused: false,
        time: formatSeconds(nextSec),
        singleInitialSec: nextSec,
      };
      emit(next);
      return next;
    });
  };

  const sequenceFinishedReadyToRestart =
    contentTimerMode === "sequence" &&
    steps.length > 0 &&
    !timerRunning &&
    !paused &&
    currentSecondsInt === 0;

  const countUpStoppedHasValue =
    runtime.mode === "single" &&
    mode === "up" &&
    !timerRunning &&
    !paused &&
    currentSecondsInt > 0;

  let startPauseButtonLabel = t("start");
  if (timerRunning) {
    startPauseButtonLabel = t("pause");
  } else if (
    sequenceFinishedReadyToRestart ||
    (mode === "down" && !timerRunning && !paused && currentSecondsInt === 0)
  ) {
    startPauseButtonLabel = t("restart");
  } else if (paused) {
    startPauseButtonLabel = t("resume");
  }

  const headAngle = effectiveHeadAngle;

  const ariaTime =
    runtime.mode === "sequence"
      ? `${currentStep?.name || ""}, ${formatSeconds(runtime.stepRemaining || 0)}`
      : runtime.time;

  return (
    <div className="mt-3 mx-auto max-w-xs bg-white border border-gray-200 rounded-xl shadow-[0_2px_8px_rgba(79,70,229,0.08)] p-3">
      {contentTimerMode === "sequence" && steps.length === 0 ? (
        <p className="text-sm text-muted-foreground mb-2">
          {t("timer_no_steps") || "Add at least one step in the activity editor."}
        </p>
      ) : null}

      {contentTimerMode === "single" && mode === "down" && allowDynamicTime && !timerRunning && !paused && (
        <div className="p-2 mb-3 bg-gray-50 rounded-lg border border-gray-200">
          <label className="flex items-center gap-1.5 mb-2 text-xs font-semibold text-gray-700">
            <svg className="w-3.5 h-3.5 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            {dynamicTimeLabel || t("choose_time") || "Choose Time"}
          </label>
          <div className="flex items-start justify-start gap-1.5">
            <div className="flex flex-col items-center">
              <input
                type="number"
                min="0"
                max="99"
                value={dynamicMinutes}
                onChange={(e) => setDynamicMinutes(e.target.value.replace(/[^0-9]/g, ""))}
                className="font-mono text-base font-bold text-center bg-white border border-gray-200 rounded-lg outline-none w-14 h-10 focus:border-primary focus:ring-2 focus:ring-primary/20"
                placeholder="00"
                aria-label={t("minutes") || "Minutes"}
              />
              <span className="mt-1 text-xs text-gray-500">{t("minutes") || "Min"}</span>
            </div>
            <div className="flex flex-col items-center justify-center pt-1">
              <div className="font-mono text-lg font-bold text-gray-400">:</div>
            </div>
            <div className="flex flex-col items-center">
              <input
                type="number"
                min="0"
                max="59"
                value={dynamicSeconds}
                onChange={(e) => {
                  let val = e.target.value.replace(/[^0-9]/g, "");
                  if (parseInt(val) > 59) val = "59";
                  setDynamicSeconds(val);
                }}
                className="font-mono text-base font-bold text-center bg-white border border-gray-200 rounded-lg outline-none w-14 h-10 focus:border-primary focus:ring-2 focus:ring-primary/20"
                placeholder="00"
                aria-label={t("seconds") || "Seconds"}
              />
              <span className="mt-1 text-xs text-gray-500">{t("seconds") || "Sec"}</span>
            </div>
            <div className="flex flex-col items-center justify-center">
              <Button type="button" className="min-h-10 px-3 text-sm" onClick={handleSetButton} disabled={!validDynamicInput}>
                {t("set_time") || "Set"}
              </Button>
            </div>
          </div>
        </div>
      )}

      <div className="flex items-center justify-center py-1">
        <div
          className="relative h-32 w-32 shrink-0"
          role="img"
          aria-label={ariaTime}
        >
          {contentTimerMode === "sequence" && steps.length > 0 && totalCycleSec > 0 ? (
            <svg className="absolute inset-0 h-full w-full" viewBox="0 0 100 100" aria-hidden>
              {segmentLayout.map((seg, i) => {
                const track = arcPath(VIEW_CX, VIEW_CY, RING_RADIUS, seg.a0, seg.a1);
                return (
                  <path
                    key={seg.id || i}
                    d={track}
                    fill="none"
                    stroke={seg.color}
                    strokeWidth={RING_STROKE}
                    strokeLinecap="butt"
                    opacity={0.28}
                  />
                );
              })}
              {segmentLayout.map((seg, i) => {
                const a0 = seg.a0;
                const a1 = headAngle >= seg.a1 ? seg.a1 : headAngle;
                const d = (headAngle > seg.a0 && a1 > a0) ? arcPath(VIEW_CX, VIEW_CY, RING_RADIUS, a0, a1) : "";
                return (
                  <path
                    key={`fill-${seg.id || i}`}
                    ref={(el) => { seqArcFillRefs.current[i] = el; }}
                    d={d}
                    fill="none"
                    stroke={seg.color}
                    strokeWidth={RING_STROKE}
                    strokeLinecap="butt"
                  />
                );
              })}
            </svg>
          ) : (
            <svg className="absolute inset-0 h-full w-full -rotate-90 text-slate-200" viewBox="0 0 100 100" aria-hidden>
              <circle
                cx={VIEW_CX}
                cy={VIEW_CY}
                r={RING_RADIUS}
                fill="none"
                stroke="currentColor"
                strokeWidth={RING_STROKE}
                strokeLinecap="round"
              />
              <circle
                ref={ringCircleRef}
                cx={VIEW_CX}
                cy={VIEW_CY}
                r={RING_RADIUS}
                fill="none"
                stroke={ringColor}
                strokeWidth={RING_STROKE}
                strokeLinecap="round"
                strokeDasharray={RING_CIRC}
                strokeDashoffset={singleRingDashOffset}
                style={{ stroke: ringColor, transition: "stroke 0.3s ease" }}
              />
            </svg>
          )}
          <div className="absolute inset-[12%] flex flex-col items-center justify-center rounded-full bg-white px-1 text-center">
            {runtime.mode === "sequence" && steps.length > 0 ? (
              <div
                className={`text-sm font-semibold leading-tight line-clamp-4 ${
                  timerRunning ? "text-gray-900" : "text-gray-700"
                }`}
              >
                {currentStep?.name || ""}
              </div>
            ) : (
              showTimerDigits && (
                <div
                  className={`font-mono text-2xl font-bold tabular-nums transition-[color] duration-300 ${
                    timeIsUp ? "text-red-600 animate-pulse" : timerRunning ? "text-primary" : "text-gray-700"
                  }`}
                >
                  {runtime.time}
                </div>
              )
            )}
          </div>
        </div>
      </div>

      <div className="flex gap-2 justify-center mt-2">
        <Button
          type="button"
          className={`min-h-10 px-5 text-sm rounded-lg ${
            timerRunning ? "bg-amber-500 text-white hover:bg-amber-600" : "bg-primary text-white hover:bg-primary/90"
          }`}
          onClick={handleStartPauseButton}
          disabled={contentTimerMode === "sequence" && steps.length === 0}
        >
          {startPauseButtonLabel}
        </Button>
        {countUpStoppedHasValue && (
          <Button
            type="button"
            variant="secondary"
            className="min-h-10 px-5 text-sm"
            onClick={handleResetCountUp}
          >
            {t("reset")}
          </Button>
        )}
        {!timeIsUp && (timerRunning || paused) && (
          <Button type="button" variant="secondary" className="min-h-10 px-5 text-sm" onClick={handleTimerStopReset}>
            {t("stop")}
          </Button>
        )}
      </div>
    </div>
  );
};

export default Timer;
